import type { GetTechnologyToolOutput } from '@azymmetric/shared/tools/get-technology.tool-config';
import { cn } from '@lib/utils';
import { Building2, Layers, Target } from 'lucide-react';

type TechnologyArtifactProps = {
  technology: GetTechnologyToolOutput;
};

export const TechnologyArtifact: React.FC<TechnologyArtifactProps> = (props) => {
  const { technology } = props;

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">{technology.name}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Layers className="size-4" />
            <span>{technology.category.name}</span>
            {technology.subcategory?.name && <span>/ {technology.subcategory.name}</span>}
          </div>
          <div className="flex items-center gap-1">
            <Building2 className="size-4" />
            <span>{technology.company.name}</span>
          </div>
        </div>
      </div>

      {/* Readiness Scores */}
      {(technology.featuredAssessments.trl || technology.featuredAssessments.mrl) && (
        <div className="grid grid-cols-2 gap-4">
          {technology.featuredAssessments.trl && (
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground">TRL Score</div>
              <div className="mt-2 text-3xl font-bold">{technology.featuredAssessments.trl.score}</div>
            </div>
          )}
          {technology.featuredAssessments.mrl && (
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground">MRL Score</div>
              <div className="mt-2 text-3xl font-bold">{technology.featuredAssessments.mrl.score}</div>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {technology.description && (
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">Description</h2>
          <p className="text-sm text-muted-foreground">{technology.description}</p>
        </div>
      )}

      {/* Technology Uses */}
      {technology.technologyUses && technology.technologyUses.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Applications</h2>
          <div className="flex flex-col gap-2">
            {technology.technologyUses.map((use) => (
              <div
                key={use.id}
                className="rounded-lg border bg-card p-3"
              >
                <div className="flex items-start gap-2">
                  <Target className="mt-0.5 size-4 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <div className="font-medium">{use.name}</div>
                    {use.description && <div className="text-sm text-muted-foreground">{use.description}</div>}
                    {use.domains && use.domains.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {use.domains.map((domain) => (
                          <span
                            key={domain.id}
                            className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                          >
                            {domain.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subsystems */}
      {technology.subsystems && technology.subsystems.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Subsystems</h2>
          <div className="grid gap-2">
            {technology.subsystems.map((subsystem) => (
              <div
                key={subsystem.id}
                className="rounded-lg border bg-card p-3"
              >
                <div className="font-medium">{subsystem.name}</div>
                {subsystem.description && (
                  <div className="mt-1 text-sm text-muted-foreground">{subsystem.description}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span className={cn('rounded px-1.5 py-0.5', 'bg-muted')}>{subsystem.type}</span>
                  {subsystem.origin && (
                    <span className={cn('rounded px-1.5 py-0.5', 'bg-muted')}>{subsystem.origin}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {technology.timeline && Object.keys(technology.timeline).length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Timeline</h2>
          <div className="rounded-lg border bg-card p-4">
            <pre className="text-xs">{JSON.stringify(technology.timeline, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
